import classNames from 'classnames'
import { memo, useEffect, useState } from 'react'

import { TEST_LOCATORS } from '@src/constants/test-locators'
import { BreadCrumb } from '@src/quince-components/src'
import Directory from '@src/quince-site-common/src/domains/Directory'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useIntersect from '@src/quince-site-common/src/hooks/useIntersect'
import siteStateManager from '@src/quince-site-common/src/redux/globalStateManager/SiteStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'

import { IContentfulCollection } from '../../types/ContentfulCollectionService.types'

import SubCategoryLinksList from './subCategoryLinksList'
import useGetSubCategoryConfig from './subCategoryLinksList/useGetSubCategoryConfig'
import styles from './subCategoryList.module.scss'

export interface LinkGroupProps {
  buttons?: IContentfulCollection[]
  className?: string
}

interface SubCategoryListProps {
  directory: Directory
  linkGroup: LinkGroupProps
  titleRenderer?: () => React.ReactNode | null
  contentful_id: string
  isNewCLPFiltersExperimentActive: boolean
  showMobileFilterWithDrawer: boolean
  showLoader?: boolean
}

const SubCategoryList = memo(
  ({
    linkGroup,
    directory,
    titleRenderer,
    contentful_id,
    isNewCLPFiltersExperimentActive,
    showMobileFilterWithDrawer,
    showLoader,
  }: SubCategoryListProps) => {
    const { viewNumber, breadCrumbListClass = '' } = useGetSubCategoryConfig()
    const { deviceType } = useDeviceInfo()
    const isMobile = deviceType === DEVICE_TYPES.MOBILE

    const [headerHeight, setHeaderHeight] = useState(0)

    const [intersectRef, isNotScrolledDown] = useIntersect(null, '0px', 0, true) as unknown as [
      React.RefObject<HTMLDivElement>,
      boolean,
    ]

    const isScrolledDown = !isNotScrolledDown
    const globalIsScrollingDown = useAppSelector<boolean>(siteStateManager, siteStateManager.getIsScrollingDown)

    const getHideBreadCrumb = () => {
      if (isNewCLPFiltersExperimentActive) {
        return directory.paths.length < 2
      }
      if (isMobile) {
        return directory.paths.length <= 1
      }
      return directory.paths.length < 1
    }

    const hideBreadCrumb = getHideBreadCrumb()

    const noLinkGroup = isMobile && (!linkGroup.buttons?.length || !linkGroup.buttons)

    const containerClasses = classNames(styles.container, {
      [`${styles.hidden_breadcrumb}`]: hideBreadCrumb,
      [`${styles.no_linkgroup}`]: noLinkGroup,
      [`${styles.new_clp_filters_experiment}`]: isNewCLPFiltersExperimentActive,
      [`${styles.no_subcategory_links_list}`]: !linkGroup.buttons?.length,
    })

    const titleContainerTopClasses = classNames(styles.title_container, {
      [`${styles.title_container_top}`]: linkGroup.buttons?.length,
      [`${styles.hidden_breadcrumb}`]: hideBreadCrumb,
    })

    const titleContainerClasses = classNames(styles.title_container, {
      [`${styles.hidden_breadcrumb}`]: hideBreadCrumb,
    })

    const subcategoryLinksListClasses = classNames(styles.subcategory_links_list, {
      [`${styles.new_clp_subcategory_links_list}`]: isNewCLPFiltersExperimentActive && linkGroup.buttons?.length,
      [`${styles.no_subcategory_links_list}`]: !linkGroup.buttons?.length,
    })

    // Get header height when component mounts and when scrolling direction changes
    useEffect(() => {
      if (isBrowser()) {
        const getHeaderHeight = () => {
          const headerElement = document.getElementById('site-primary-header')
          const headerHeight = headerElement?.getBoundingClientRect().height

          setHeaderHeight(headerHeight ?? 0)
        }

        getHeaderHeight()
        window.addEventListener('resize', getHeaderHeight)

        return () => {
          window.removeEventListener('resize', getHeaderHeight)
        }
      }
    }, [globalIsScrollingDown])

    return (
      <>
        <div
          className={classNames(containerClasses, {
            [`${styles.fixedSubCategoryListForMobileGroupA}`]:
              isMobile && isScrolledDown && !globalIsScrollingDown && showMobileFilterWithDrawer,
          })}
          data-active-view={viewNumber}
          style={
            showMobileFilterWithDrawer && isScrolledDown && !globalIsScrollingDown
              ? { top: `${headerHeight}px` }
              : undefined
          }
          id="sub-category-list-header"
        >
          <div className={styles.container__text}>
            {!isNewCLPFiltersExperimentActive && isMobile && titleRenderer && (
              <div className={titleContainerTopClasses}>{titleRenderer()}</div>
            )}
            {!hideBreadCrumb && (
              <div data-testid={TEST_LOCATORS.COLLECTION_BREADCRUMB}>
                <BreadCrumb
                  directory={directory}
                  breadCrumbListClass={breadCrumbListClass}
                  isNewCLPFiltersExperimentActive={isNewCLPFiltersExperimentActive}
                  deviceType={deviceType}
                  showLoader={showLoader}
                />
              </div>
            )}
            {isNewCLPFiltersExperimentActive && titleRenderer && (
              <div className={titleContainerClasses}>{titleRenderer()}</div>
            )}
          </div>
          <div className={subcategoryLinksListClasses}>
            <SubCategoryLinksList
              linkGroup={linkGroup}
              contentful_id={contentful_id}
              isNewCLPFiltersExperimentActive={isNewCLPFiltersExperimentActive}
              showLoader={showLoader && !!linkGroup.buttons?.length}
            />
          </div>
        </div>
        <div ref={intersectRef}></div>
      </>
    )
  },
)

SubCategoryList.displayName = 'SubCategoryList'

export default SubCategoryList
